
import { useStore } from '@/composables/use-store'
import { Translation } from '@/types/i18n'
import { computed, defineComponent, PropType } from 'vue'
import MediaPickerDialog from '@/components/MediaPickerDialog.vue'

/**
   Media picker component that edits the given
   translation key's value for all available languages.

   Shows a media picker for each language side by side.
   Translation key is displayed below the pickers.

   Props:
   - translationKey: The i18n key to edit
   - label: Label shown above the media pickers
   - targetWidth: Recommended image width (optional)
   - targetHeight: Recommended image height (optional)
 **/
export default defineComponent({
  name: 'MultiTranslatedMediaPicker',
  components: {
    MediaPickerDialog
  },
  props: {
    translationKey: {
      type: String as PropType<string>,
      required: true
    },
    label: {
      type: String as PropType<string>,
      required: true
    },
    targetWidth: {
      type: Number as PropType<number | null>,
      default: null
    },
    targetHeight: {
      type: Number as PropType<number | null>,
      default: null
    }
  },
  setup (props) {
    const store = useStore()

    const localesMap: Record<string, Translation> =
      store.getters['campaigns/currentLocales']

    const allLocaleCodes = computed<string[]>(() => {
      return Object.keys(localesMap)
    })

    const translatedValues = computed(() => {
      const values: Record<string, string> = {}
      for (const localeCode of allLocaleCodes.value) {
        Object.defineProperty(values, localeCode, {
          get (): string {
            const message =
              localesMap[localeCode]?.messages[props.translationKey]
            return (message as string) || ''
          },
          set (value: string) {
            store.commit('campaigns/UPDATE_TRANSLATION_MESSAGE', {
              locale: localeCode,
              messageId: props.translationKey,
              value: value
            })
          },
          enumerable: true
        })
      }
      return values
    })

    return {
      translatedValues,
      allLocaleCodes,
      localesMap
    }
  }
})
